import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import  {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getFormValues, isAsyncValidating, touch, submit, change } from "redux-form";

import { useModalStack } from "utils/common";
import { FORM_BY_ADTYPE } from "social/constants/forms";
import { adTypeLabelSort } from 'social/utils';
import { getValidationByAdType, getSelectedAdTypesWithPlatforms, isWizardValid } from 'selectors/Social2';

import { SOCIAL_WIZARD } from "forms/list";
import { ADD_CONTENT_INDEX, ADTYPE_LABELS } from "./constants/common";

import Breadcrumb from './Breadcrumb';
import SocialPlatformTabs from './SocialPlatformTabs';
import AdTypePill from "./AdTypePill";
import SharedContent from './formats/SharedContent';

import "./AddContentModal.css";


export default function AddContentModal(props){
	const {
		onClose,
		onBack,
	} = props;

	const dispatch = useDispatch();

	const values = useSelector(getFormValues(SOCIAL_WIZARD));
	const { isSaving } = useSelector(state => state.conceptDetails);
	const isFormValid = useSelector(isWizardValid);
	const isValidating = useSelector(isAsyncValidating(SOCIAL_WIZARD));
	const adTypes = useSelector(getSelectedAdTypesWithPlatforms);

	const sortedAdTypes = useMemo(() => {
		return [...adTypes].sort(adTypeLabelSort);
	}, [adTypes]);

	const validationByAdType = useSelector(getValidationByAdType);
	const currentFieldNames = useSelector((state) => {
		const { registeredFields = {} } = state.form[SOCIAL_WIZARD] || {};
		return Object.keys(registeredFields).filter(key => registeredFields[key]?.count);
	});

	const [adTypeIndex, setAdTypeIndex] = useState((adTypes.length > 1) ? -1 : 0); /*
		If there's only one adType, make it selected initially.
		Otherwise, set the index to -1 to signify that none are selected, which will default
		to displaying the Shared Content tab.
	*/
	const scrollRef = useRef();
	useEffect(() => {
		if (!scrollRef.current) { return; }

		scrollRef.current.scrollTo?.(0, 0);
	}, [adTypeIndex]);

	const { libraryId } = useParams();
	const { conceptName, conceptId, shared } = values || {};
	const { assetType } = shared || {};

	useEffect(() => {
		if(!assetType){
			dispatch(change(SOCIAL_WIZARD, "shared.assetType", "prebuilt"));
		}
	}, [assetType]);

	const modalStackProps = {
		title: `${conceptId ? "Add Social Creative to Concept" : "Social Concept"} - ${conceptName}`,
		setAppTitle: false,
		type: "modal",
		className: "social-modal white-title-bar add-content-modal",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
	};
	const [modalStack, navigationActions, ModalStackHead] = useModalStack(modalStackProps);

	const commonModalProps = {
		...modalStackProps,
		onClose: () => {
			if (modalStack.length) {
				navigationActions.popModal();
			} else {
				onClose();
			}
		},
	};

	const selectedAdType = sortedAdTypes[adTypeIndex];

	const FormForAdType = FORM_BY_ADTYPE[selectedAdType?.adType] || SharedContent;
	const tabAdTypes = useMemo(() => {
		return sortedAdTypes.filter(item => selectedAdType?.platform === item.platform);
	}, [sortedAdTypes, selectedAdType]);

	const updateAdTypeIndex = useCallback((newIndex) => {
		dispatch(touch(SOCIAL_WIZARD, ...currentFieldNames));
		setAdTypeIndex(newIndex);
	}, [currentFieldNames]);

	function onTabChange(newTab){
		const { value } = newTab;
		updateAdTypeIndex(sortedAdTypes.findIndex(item => item.platform === value));
	}

	const tabProps = {
		adTypes: sortedAdTypes,

		selectedAdType,
		onTabChange,
	};

	function onSave(){
		dispatch(submit(SOCIAL_WIZARD));
	}

	const isFirstForm = (adTypeIndex === -1) || ((adTypes.length === 1) && (adTypeIndex === 0));
	const isLastForm = adTypeIndex === (adTypes.length - 1);

	function goToNextCreative(){
		updateAdTypeIndex(adTypeIndex + 1);
	}

	function goToPrevCreative(){
		updateAdTypeIndex(adTypeIndex - 1);
	}

	const typeSelect = useMemo(() => {
		if(!tabAdTypes.length){
			return null;
		}
		const $pills = tabAdTypes.map((type) => {
			const { adType } = type || {};
			const indexForType = sortedAdTypes.findIndex(item => item.adType === adType);
			const name = ADTYPE_LABELS[adType];
			const qaSelector = `cypress-${adType}-pill`;
			return (
				<AdTypePill
					key = {adType}
					name = {name}
					className={qaSelector}
					isSelected = {adType === selectedAdType.adType}
					{...validationByAdType[adType]}
					onClick = {() => {
						updateAdTypeIndex(indexForType);
					}}
				/>
			);
		});

		return <div className="pills-container">{$pills}</div>;
	}, [tabAdTypes, selectedAdType, sortedAdTypes, validationByAdType, updateAdTypeIndex]);

	const $breadcrumb = <Breadcrumb currentStep={ADD_CONTENT_INDEX} />;
	function renderContent(){
		return (
			<div>
				{$breadcrumb}
				<div className = "main-content-container">
					<ProgressIndicator isLoading={isSaving || isValidating} />
					<SocialPlatformTabs {...tabProps} />
					{typeSelect}
					<div ref={scrollRef} className={classNames("scroll-container", { "with-pill": Boolean(typeSelect) })}>
						<FormForAdType
							navigationActions={navigationActions}
							libraryId={libraryId}
						 />
					</div>
				</div>
			</div>
		);
	}

	function renderFooter(){
		const backAction = isFirstForm ? onBack : goToPrevCreative;
		return (
			<>
				<Button theme="default" onClick={backAction}>Back</Button>
				<div className="right-buttons">
					<Button
						theme = "green"
						form = {SOCIAL_WIZARD}
						type = "submit"
						onClick = {onSave}
						disabled = {!isFormValid || isSaving || isValidating}
					>
						{conceptId ? "Update" : "Create"} Concept
					</Button>
					{!isLastForm ? <Button theme="blue" onClick={goToNextCreative}>Next Creative</Button> : null}
				</div>
			</>
		);
	}

	let modalProps = {
		...commonModalProps,
		renderContent,
		renderFooter,
	};

	if (modalStack.length) {
		return <ModalStackHead Breadcrumb={$breadcrumb} commonModalProps={commonModalProps} />;
	}

	return <Modal {...modalProps} />;
}
